import { useCallback, useState } from 'react';
import { To } from 'react-router-dom';
import { twJoin } from 'tailwind-merge';

import { Icon, RyeconCaretDown, RyeconList, Tooltip } from '@dbt-labs/sourdough';

import { NavButtonOption, OptionConfig } from './NavButtonGroup';

export type OptionCollapsibleConfig<TOption extends string = string> = {
  children: OptionConfig<TOption>[];
} & OptionConfig<TOption>;

interface NavButtonCollapsibleGroupParams<TOption extends string> {
  primaryOption?: {
    option: TOption;
    to: To;
  };
  options: OptionCollapsibleConfig<TOption>[];
  currentOption: [parentOption: TOption, childOption?: TOption] | undefined;
  getTargetUrl: (...option: TOption[]) => string;
}

interface CollapsibleOptionButtonParams<TOption extends string> {
  currentOption: [parentOption: TOption, childOption?: TOption] | undefined;
  config: OptionCollapsibleConfig<TOption>;
  getTargetUrl: (...option: TOption[]) => string;
}

const NavButtonCollapsibleOption = <TOption extends string>({
  currentOption,
  config,
  getTargetUrl,
}: CollapsibleOptionButtonParams<TOption>) => {
  const isSelected = currentOption?.[0] === config.option;

  const [isOpen, setIsOpen] = useState<boolean>(isSelected);
  const onToggle = useCallback(() => setIsOpen((current) => !current), [setIsOpen]);

  const displayText = config.label || config.option;
  const showBackground = isSelected && !isOpen;
  return (
    <>
      <button
        className={twJoin(
          'flex h-9 w-full cursor-pointer items-center space-x-2 rounded-lg p-2 text-left align-middle text-fgAlt transition-colors duration-200 motion-reduce:duration-0',
          showBackground && 'bg-bgBrandMuted text-fgMain',
          !showBackground && 'bg-transparent',
        )}
        onClick={onToggle}
      >
        <span className="flex-0 m-auto w-4">{config.icon}</span>
        <span className="flex-1 overflow-hidden">
          <Tooltip content={displayText} displayOnlyWhenTruncated>
            {(ref) => (
              <span ref={ref} className="block truncate">
                {displayText}
              </span>
            )}
          </Tooltip>
        </span>

        <Icon
          ryecon={RyeconCaretDown}
          className={twJoin(
            'flex-0 h-4 w-4 text-fgAlt transition-transform duration-150 ease-in-out motion-reduce:duration-0',
            isOpen && 'rotate-180',
          )}
        />
      </button>

      {isOpen && (
        <div className="ml-4 flex flex-col py-px">
          {config.children.map((childConfig, index) => (
            <NavButtonOption
              key={childConfig.option}
              config={childConfig}
              to={getTargetUrl(config.option, childConfig.option)}
              currentOption={currentOption?.[1]}
              index={index}
            />
          ))}
        </div>
      )}
    </>
  );
};

export function NavButtonCollapsibleGroup<TOption extends string>({
  primaryOption,
  options,
  currentOption,
  getTargetUrl,
}: NavButtonCollapsibleGroupParams<TOption>) {
  return (
    <div className="p-1">
      {primaryOption && (
        <NavButtonOption
          key={primaryOption.option}
          config={{
            option: primaryOption.option,
            icon: <Icon ryecon={RyeconList} />,
          }}
          to={primaryOption.to}
          currentOption={currentOption?.[0]}
          index={0}
        />
      )}
      {options.map((config) => (
        <NavButtonCollapsibleOption
          key={config.option}
          config={config}
          currentOption={currentOption}
          getTargetUrl={getTargetUrl}
        />
      ))}
    </div>
  );
}
